// C:\Users\phill\paperplay\frontend\src\features\account\usersEndpoints.ts

/**
 * Users Endpoints (backend-aligned)
 * ----------------------------------------------------------------------------
 * Source of truth: backend/features/accounts/urls.py
 *
 * Confirmed routes (mounted under /api/*; some also available under /auth/*):
 *  - /api/current-user/                      (CurrentUserView)
 *  - /api/register/ and /api/users/register/ (UserRegistrationView; alias)
 *  - /api/list-users/                        (ListUsersView)
 *  - /api/users/admin/                       (AdminUserViewSet via router)
 *  - /api/users/admin/{id}/activate/         (AdminUserViewSet @action)
 *  - /api/users/admin/{id}/deactivate/       (AdminUserViewSet @action)
 *  - /api/users/me/location/                 (UpdateUserLocationView)
 *
 * Notes:
 * - There is NOT a generic /api/users/ list/detail route in accounts/urls.py.
 * - Keep trailing slashes (Django-friendly).
 */

import { api, withQuery, type QueryVal } from '../../apis/endpoints/_helpers';
import type { UUID } from '../../types/commonTypes';
import { DASHBOARD } from '../dashboard/dashboardEndpoints';
import { RBAC } from '../rbac/rbacEndpoints';

type QS = Readonly<Record<string, QueryVal>>;

/** Encode a path segment safely. */
const enc = (v: string | number) => encodeURIComponent(String(v));

/* Base constants (DRY) */
const CURRENT_USER = api('/current-user/');
const ME_LOCATION = api('/users/me/location/');

const REGISTER = api('/register/');
const REGISTER_ALIAS = api('/users/register/');

const LIST_USERS = api('/list-users/');
const ADMIN_BASE = api('/users/admin/');
const ADMIN_DETAIL = (userId: string | UUID) => api(`/users/admin/${enc(userId)}/`);
const DASHBOARD_ACTIVITY_DETAIL = (activityId: string | UUID) =>
  api(`/dashboard/activities/${enc(activityId)}/`);

export const USERS = {
  // ---------------------------------------------------------------------------
  // Auth/account "me"
  // ---------------------------------------------------------------------------
  CURRENT_USER,

  /**
   * Update current user's geo/location.
   * Backend route is mounted under API_URLPATTERNS.
   */
  ME_LOCATION,

  // ---------------------------------------------------------------------------
  // Registration (canonical + back-compat alias exists server-side)
  // ---------------------------------------------------------------------------
  /**
   * Canonical registration endpoint.
   * Prefer AUTH.REGISTER for auth flows, but this is safe for legacy callers.
   */
  REGISTER,

  /**
   * Back-compat alias: backend maps users/register/ → register/.
   */
  REGISTER_ALIAS,

  // ---------------------------------------------------------------------------
  // Admin / staff user listing + admin CRUD
  // ---------------------------------------------------------------------------
  /**
   * Admin-only convenience listing view (paginated).
   * This is a plain view, not a router ViewSet.
   */
  LIST_USERS,
  LIST_USERS_QS: (params: QS) => withQuery(LIST_USERS, params),

  /**
   * Admin CRUD for users via DRF router.
   * IMPORTANT: router registers "users/admin" (NOT "admin/users").
   */
  ADMIN: {
    BASE: ADMIN_BASE,
    LIST: ADMIN_BASE,
    LIST_QS: (params: QS) => withQuery(ADMIN_BASE, params),

    DETAIL: ADMIN_DETAIL,

    // AdminUserViewSet custom actions (confirmed in backend)
    ACTIVATE: (userId: string | UUID) => api(`/users/admin/${enc(userId)}/activate/`),
    DEACTIVATE: (userId: string | UUID) => api(`/users/admin/${enc(userId)}/deactivate/`),
  },

  // ---------------------------------------------------------------------------
  // Legacy keys used across the frontend (map to real endpoints)
  // ---------------------------------------------------------------------------
  /** Admin list (alias to USERS.ADMIN.LIST). */
  BASE: ADMIN_BASE,

  /** Admin detail (alias to USERS.ADMIN.DETAIL). */
  DETAIL: ADMIN_DETAIL,

  /** Alias used by older dashboard utilities (admin list/detail). */
  DASHBOARD_BASE: ADMIN_BASE,
  DASHBOARD_DETAIL: ADMIN_DETAIL,

  /**
   * Permissions list. Backend exposes global permissions under /api/permissions/.
   * userId is ignored (kept for backward compatibility with callers).
   */
  PERMISSIONS: (_userId: string | UUID) => RBAC.PERMISSIONS(),

  /**
   * Activity feed. Backend provides /api/dashboard/activities/.
   * userId is ignored (activity scope is enforced server-side).
   */
  ACTIVITIES: (_userId: string | UUID) => DASHBOARD.ACTIVITIES,
  ACTIVITY_DETAIL: (_userId: string | UUID, activityId: string | UUID) =>
    DASHBOARD_ACTIVITY_DETAIL(activityId),

  /**
   * No dedicated user growth endpoint is defined in backend urls yet.
   * Placeholder mapped to the admin stats endpoint until a real growth API exists.
   */
  GROWTH: DASHBOARD.ADMIN_STATS,
} as const;

export type UsersEndpoints = typeof USERS;
